import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdEdit, MdCheck, MdInfo } from 'react-icons/md';

function PollExplanationInput({ pollId, customPollId, existingExplanation, onSave, disabled = false }) {
  const [explanation, setExplanation] = useState(existingExplanation || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setExplanation(existingExplanation || '');
  }, [existingExplanation]);

  // Word count helper
  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const wordCount = getWordCount(explanation);
  const maxWords = 500;
  const isOverLimit = wordCount > maxWords;

  const handleSave = async () => {
    if (!explanation.trim()) {
      setError('Explanation cannot be empty');
      return;
    }

    if (isOverLimit) {
      setError('Explanation exceeds 500 word limit');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      await axios.post(`${API_URL}/api/representative/explanation`, {
        poll_id: pollId || null,
        custom_poll_id: customPollId || null,
        explanation: explanation.trim()
      });

      setSaved(true);
      setIsEditing(false);
      if (onSave) onSave(explanation);
      
      // Clear saved indicator after 2 seconds
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save explanation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setExplanation(existingExplanation || '');
    setIsEditing(false);
    setError('');
  };

  if (disabled) {
    return null;
  }

  return (
    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="poll-explanation-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MdInfo className="text-blue-600" />
          <span className="text-sm font-medium text-blue-800">
            Representative Explanation
          </span>
        </div>
        {!isEditing && existingExplanation && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <MdEdit className="text-sm" />
            Edit
          </button>
        )}
        {saved && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            <MdCheck />
            Saved
          </span>
        )}
      </div>

      {isEditing || !existingExplanation ? (
        <div className="space-y-2">
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Explain your position on this issue (visible to voters)..."
            className={`w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isOverLimit ? 'border-red-300' : 'border-gray-300'
            }`}
            rows={4}
            data-testid="explanation-textarea"
          />
          
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {wordCount}/{maxWords} words
            </span>
            
            <div className="flex items-center gap-2">
              {existingExplanation && (
                <button
                  onClick={handleCancel}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving || isOverLimit || !explanation.trim()}
                className="px-4 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="save-explanation-btn"
              >
                {saving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <MdCheck />
                    Save Explanation
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
          <p className="italic">"{existingExplanation}"</p>
        </div>
      )}

      <p className="text-xs text-blue-600 mt-2">
        As a representative, your explanation will be visible to voters viewing your profile.
      </p>
    </div>
  );
}

export default PollExplanationInput;
